import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/language-toggle";
import { useLanguage } from "@/components/language-provider";
import { BrandText } from "@/components/brand-text";
import { VideoModal } from "@/components/landing/video-modal";
import { SignatureFlowMock, PcfShowcase, TemplateMappingMock, GovBrFlowMock, BrandPair, VideoFacade, StepsInteractive } from "@/components/landing/product-mocks";
import { useEffect } from "react";
import { Reveal } from "@/components/landing/reveal";
import { Stage3D, Words, Marquee } from "@/components/landing/motion";
import { ParallaxHero } from "@/components/landing/parallax-hero";
import { CinematicScroll } from "@/components/landing/cinematic-scroll";
import { SalesHero } from "@/components/landing/sales-hero";
import { DynamicsImmersive } from "@/components/landing/dynamics-immersive";
import { DynamicsSwitch, D365Button } from "@/components/landing/dynamics-switch";
import { useSmoothScroll } from "@/lib/smooth-scroll";

// "jungle" = full-screen parallax opening + signature scene below; "classic" = the previous two-column hero;
// "cinematic" = scroll-scrubbed film that absorbs the hero, the signature scene and #how, then hands off into #dynamics.
// Flip this one constant to go back ("voltar ao que era antes do jungle").
type HeroVariant = "jungle" | "classic" | "cinematic" | "sales";
const heroVariantS: HeroVariant[] = ["jungle", "classic", "cinematic", "sales"];
// "sales" = sales-first opening (ZapSign/DocuSign pattern) with the film as the next section.
// The admin panel can override this (website settings → heroVariant) without a deploy; this is only the default.
const DEFAULT_HERO: HeroVariant = "sales";
// Website settings (admin panel): hero variant, free-signup toggle and copy overrides. Cached so repeat visits don't flash.
type WebsiteSettings = { showFreeSignup?: boolean; heroVariant?: string; copy?: Partial<Record<"pt" | "en", Record<string, string>>> };
const SETTINGS_CACHE = "fs.websiteSettings";
function cachedSettings(): WebsiteSettings {
  try { return JSON.parse(localStorage.getItem(SETTINGS_CACHE) || "{}") as WebsiteSettings; } catch { return {}; }
}
const SMOOTH_SCROLL = true; // wheel inertia (desktop only); false = the browser's native scrolling
import { getApiUrl } from "@/lib/config";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { WEBSITE_ROUTES, APP_URL } from "@/lib/config";
import { usePlans } from "@/lib/use-plans";
import {
  Menu, X, Play, Check, ShieldCheck, Lock, Landmark, Upload, PenLine, Sparkles,
  FileCheck2, MessageCircle, Code2, Building2, Clock3, Languages, Scale,
  Home, Users, Calculator, Briefcase, Eye, Send, type LucideIcon,
} from "lucide-react";

type IconRow = [LucideIcon, string];
type IconCard = [LucideIcon, string, string];

const WHATSAPP_URL = (import.meta.env.VITE_WHATSAPP_URL as string | undefined) || "";

// YouTube videos per language (FillSign channel). The site language picks the version;
// anything other than pt falls back to English.
const VIDEOS = {
  pt: { intro: "n-X8PAnzmcI", d365: "kj-qT9FmzWU" },
  en: { intro: "SPeM4tMQuVM", d365: "qIGd0KSpj5U" },
} as const;
const youtube = (id: string) => `https://www.youtube.com/embed/${id}`;

// Brand tokens live in index.css (--fs-*). Tailwind arbitrary values keep the page self-contained.
const ink = "text-[#1B2A1E]";
const forest = "text-[var(--fs-forest)]";
const cta = "bg-[var(--fs-moss)] hover:bg-[var(--fs-forest)] text-white";

function NavLinks({ pt, onClick }: { pt: boolean; onClick?: () => void }) {
  const cls = "text-sm text-gray-600 hover:text-gray-900 transition-colors";
  return (
    <>
      <a href="#how" className={cls} onClick={onClick}>{pt ? "Como funciona" : "How it works"}</a>
      <a href="#features" className={cls} onClick={onClick}>{pt ? "Recursos" : "Features"}</a>
      <a href="#dynamics" className={cls} onClick={onClick}>Dynamics 365</a>
      <Link href={WEBSITE_ROUTES.pricing} className={cls} onClick={onClick}>{pt ? "Preços" : "Pricing"}</Link>
      <Link href={WEBSITE_ROUTES.apiDocs} className={cls} onClick={onClick}>API</Link>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────

export default function Landing() {
  const { language } = useLanguage();
  const pt = language === "pt";
  const [menuOpen, setMenuOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const { plans: apiPlans } = usePlans();
  const videos = pt ? VIDEOS.pt : VIDEOS.en;
  // Admin panel → website settings: free-signup toggle, hero variant and copy overrides (see docs/website-settings-contract.md).
  const [settings, setSettings] = useState<WebsiteSettings>(cachedSettings);
  useEffect(() => {
    fetch(getApiUrl("/api/v1/website-settings")).then((r) => (r.ok ? r.json() : null)).then((d: WebsiteSettings | null) => {
      if (!d) return;
      setSettings(d);
      try { localStorage.setItem(SETTINGS_CACHE, JSON.stringify(d)); } catch { /* storage may be unavailable */ }
    }).catch(() => {});
  }, []);
  const signup = settings.showFreeSignup !== false;
  const heroVariant: HeroVariant = heroVariantS.includes(settings.heroVariant as HeroVariant) ? (settings.heroVariant as HeroVariant) : DEFAULT_HERO;
  const film = heroVariant === "cinematic" || heroVariant === "sales";
  const copy = (key: string, fallback: string) => settings.copy?.[pt ? "pt" : "en"]?.[key]?.trim() || fallback;
  const ctaHref = signup ? `${APP_URL}/register` : WEBSITE_ROUTES.enterpriseContact;
  const ctaLabel = signup ? (pt ? "Criar conta grátis" : "Create free account") : (pt ? "Falar com vendas" : "Talk to sales");

  const segments: IconRow[] = pt
    ? [[Scale, "Escritórios de advocacia"], [Home, "Imobiliárias"], [Users, "RH e onboarding"], [Calculator, "Contabilidades"], [Briefcase, "Times de vendas"], [Building2, "Empresas no Dynamics 365"]]
    : [[Scale, "Law firms"], [Home, "Real estate"], [Users, "HR & onboarding"], [Calculator, "Accounting"], [Briefcase, "Sales teams"], [Building2, "Dynamics 365 teams"]];

  const features: IconCard[] = pt
    ? [
        [MessageCircle, "Assinatura pelo WhatsApp", "O canal que o brasileiro responde. Envio, lembrete e link de assinatura direto na conversa."],
        [PenLine, "Posicionamento por IA", "A assinatura vai em cima da linha de assinatura, com o nome embaixo. Em qualquer tamanho de página."],
        [Landmark, "GOV.BR e ICP-Brasil", "Quando o documento exige assinatura gov.br ou certificado ICP-Brasil, o fluxo continua no FillSign, sem trocar de ferramenta."],
        [Building2, "Nativo no Dynamics 365", "Componente PCF dentro do CRM: envie e acompanhe assinaturas na própria oportunidade ou conta."],
        [Code2, "API e webhooks", "Integre em qualquer sistema. Crie envelopes, consulte status e receba eventos em tempo real."],
        [Languages, "Português e inglês", "Interface, e-mails e página de assinatura nos dois idiomas, por signatário."],
      ]
    : [
        [MessageCircle, "Sign over WhatsApp", "The channel people actually answer. Delivery, reminders and the signing link in the chat."],
        [PenLine, "AI signature placement", "The signature lands on the signature line, name below it. Any page size."],
        [Landmark, "GOV.BR and ICP-Brasil", "When a document requires a gov.br signature or an ICP-Brasil certificate, the flow stays inside FillSign."],
        [Building2, "Native in Dynamics 365", "A PCF component inside your CRM: send and track signatures from the opportunity or account."],
        [Code2, "API and webhooks", "Plug into any system. Create envelopes, query status and receive events in real time."],
        [Languages, "Portuguese and English", "Interface, emails and signing page in both languages, per signer."],
      ];

  const plans = (apiPlans && apiPlans.length > 0
    ? [...apiPlans]
        .sort((a, b) => (a.contactSales ? 1 : 0) - (b.contactSales ? 1 : 0) || a.priceMonthlyBrl - b.priceMonthlyBrl)
        .slice(0, 3)
        .map((pl) => ({
          name: pl.name,
          price: pl.contactSales ? (pt ? "Sob consulta" : "Custom") : pt ? `R$ ${pl.priceMonthlyBrl}` : `$${pl.priceMonthlyUsd}`,
          period: pl.contactSales ? "" : pt ? "/mês" : "/month",
          desc: pt ? pl.descriptionPt : pl.descriptionEn,
          highlight: pl.popular,
          contactSales: pl.contactSales,
        }))
    : [
        { name: pt ? "Grátis" : "Free", price: pt ? "R$ 0" : "$0", period: pt ? "/mês" : "/month", desc: pt ? "Para começar a assinar hoje." : "To start signing today.", highlight: false, contactSales: false },
        { name: "Pro", price: pt ? "R$ 199" : "$39", period: pt ? "/mês" : "/month", desc: pt ? "Para times com volume mensal." : "For teams with monthly volume.", highlight: true, contactSales: false },
        { name: "Enterprise", price: pt ? "Sob consulta" : "Custom", period: "", desc: pt ? "Volume alto, SLA e integrações." : "High volume, SLA and integrations.", highlight: false, contactSales: true },
      ]);

  const faq: Array<[string, string]> = pt
    ? [
        ["A assinatura tem validade jurídica?", "Sim. As assinaturas eletrônicas do FillSign seguem a MP 2.200-2/2001 e a Lei 14.063/2020, com trilha de auditoria completa: IP, data/hora, e-mail, autenticação e hash do documento. Quando o documento exige mais, o signatário pode assinar no portal gov.br (assinatura avançada) ou com certificado ICP-Brasil (assinatura qualificada) e devolver o PDF pelo mesmo link."],
        ["Preciso de cartão de crédito para começar?", "Não. O plano inicial é gratuito e não pede cartão. Você só paga se decidir crescer de plano."],
        ["Como o signatário recebe o documento?", "Por e-mail e/ou WhatsApp, com um link seguro e exclusivo. Ele assina de qualquer dispositivo, sem criar conta."],
        ["Meus documentos ficam seguros?", "Sim. Criptografia AES-256 em repouso, TLS 1.3 em trânsito, infraestrutura em nuvem com backups e conformidade com a LGPD."],
        ["Funciona com o Dynamics 365?", "Sim. O FillSign tem um componente nativo (PCF) que roda dentro do CRM: envie e acompanhe assinaturas sem sair do Dynamics, em qualquer entidade, inclusive customizadas."],
        ["Posso cancelar quando quiser?", "Pode. Os planos são mensais, sem fidelidade. Cancelando, você mantém o acesso até o fim do ciclo já pago."],
      ]
    : [
        ["Are the signatures legally valid?", "Yes. FillSign electronic signatures follow Brazilian law (MP 2.200-2/2001 and Law 14.063/2020) with a full audit trail: IP, timestamp, email, authentication and document hash. When a document requires more, the signer can sign on the gov.br portal (advanced signature) or with an ICP-Brasil certificate (qualified signature) and return the PDF through the same link."],
        ["Do I need a credit card to start?", "Not in Brazil. Accounts outside Brazil validate a card with no charge (fraud protection). You only pay if you decide to upgrade."],
        ["How does the signer receive the document?", "Via email and/or WhatsApp with a secure, unique link. They sign from any device, no account needed."],
        ["Is my data secure?", "Yes. AES-256 encryption at rest, TLS 1.3 in transit, cloud infrastructure with backups and LGPD compliance."],
        ["Does it work with Dynamics 365?", "Yes. FillSign ships a native PCF component that runs inside your CRM: send and track signatures without leaving Dynamics, on any entity, including custom ones."],
        ["Can I cancel anytime?", "Yes. Plans are monthly with no lock-in. If you cancel, you keep access until the end of the paid cycle."],
      ];

  // Cinematic variant: over the film the header turns frosted and slides away while scrolling down; it returns on scroll up.
  useSmoothScroll(film && SMOOTH_SCROLL);
  const [overFilm, setOverFilm] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  useEffect(() => {
    if (!film) return;
    let lastY = window.scrollY;
    const onScroll = () => {
      const film = document.querySelector<HTMLElement>('[data-cinematic="film"]');
      const r = film?.getBoundingClientRect();
      const inFilm = !!film && !!r && r.top < 64 && r.bottom > 64 && film.dataset.phase !== "zoom";
      const y = window.scrollY;
      setOverFilm(inFilm);
      setHeaderHidden(inFilm && y > lastY && y > 80);
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Templates (used next to Dynamics in the cinematic variant).
  const templatesSection = (
    <section id="templates" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-20 sm:px-8 lg:py-28">
      <Reveal className="max-w-2xl">
        <h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>
          <Words text={pt ? "Templates que se preenchem com os dados do CRM" : "Templates that fill themselves from CRM data"} />
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          {pt
            ? "Monte o contrato uma vez no FillSign com campos como nome da conta, CNPJ e valor da oportunidade. No Dynamics, cada envio já sai preenchido com o registro aberto: nada de copiar e colar, nada de erro de digitação."
            : "Build the contract once in FillSign with fields like account name, tax ID and opportunity amount. In Dynamics, every send goes out filled from the open record: no copy-paste, no typos."}
        </p>
      </Reveal>
      <Reveal className="mt-12 min-w-0" variant="scale"><TemplateMappingMock pt={pt} /></Reveal>
    </section>
  );

  // Who it's for + Features: rendered before #dynamics (jungle/classic) or after it (cinematic).
  const whoSection = (
    <section className="border-y border-black/5 bg-[#F7FAF3]">
      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <div className="mb-3 text-sm text-gray-500">{pt ? "Feito para quem assina todo dia" : "Built for teams that sign every day"}</div>
        <Marquee>
          {segments.map(([Icon, label]) => (
            <span key={label} className="flex items-center gap-2 text-base text-gray-700">
              <Icon className="h-4 w-4 text-[var(--fs-moss)]" />
              {label}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );

  const featuresSection = (
    <section id="features" className="scroll-mt-24 border-y border-black/5 bg-[#F7FAF3]">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-28">
        <Reveal className="max-w-2xl">
          <h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>
            <Words text={pt ? "O que muda em relação a uma assinatura comum" : "What's different from a plain e-signature"} />
          </h2>
        </Reveal>
        <Reveal className="mt-12" stagger><ul className="grid gap-x-12 md:grid-cols-2">
          {features.map(([Icon, title, desc]) => (
            <li key={title} className="flex gap-4 border-t border-black/10 py-6">
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--fs-moss)]" />
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="mt-1.5 leading-relaxed text-gray-600">{desc}</p>
              </div>
            </li>
          ))}
        </ul></Reveal>
      </div>
    </section>
  );

  return (
    <div className={`min-h-screen bg-white ${ink}`}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className={`sticky top-0 z-50 border-b backdrop-blur transition-[transform,background-color,border-color] duration-300 ${
        overFilm ? "border-transparent bg-white/35 backdrop-blur-md" : "border-black/5 bg-white/90"} ${headerHidden && !menuOpen ? "-translate-y-full" : ""}`}>
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 sm:px-8">
          <Link href="/" className="flex shrink-0 items-center gap-2">
            <img src="/favicon.png" alt="" className="h-8 w-8" />
            <BrandText className="text-lg" />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            <NavLinks pt={pt} />
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <LanguageToggle />
            <a href={`${APP_URL}/login`} className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900">
              {pt ? "Entrar" : "Sign in"}
            </a>
            <a href={ctaHref} data-testid="cta-header">
              <Button className={`${cta} h-9 rounded-md px-4 text-sm`}>{ctaLabel}</Button>
            </a>
          </div>

          <button className="p-2 text-gray-700 lg:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu" aria-expanded={menuOpen}>
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="flex flex-col gap-4 border-t border-black/5 bg-white px-6 py-5 lg:hidden">
            <NavLinks pt={pt} onClick={() => setMenuOpen(false)} />
            <div className="flex items-center gap-3 pt-2">
              <LanguageToggle />
              <a href={`${APP_URL}/login`} className="flex-1">
                <Button variant="outline" className="w-full">{pt ? "Entrar" : "Sign in"}</Button>
              </a>
              <a href={ctaHref} className="flex-1">
                <Button className={`w-full ${cta}`}>{signup ? (pt ? "Criar conta" : "Sign up") : ctaLabel}</Button>
              </a>
            </div>
          </div>
        )}
      </header>

      {heroVariant === "jungle" && (
        <>
          <ParallaxHero pt={pt} ctaHref={ctaHref} ctaLabel={ctaLabel} onVideo={() => setVideoUrl(youtube(videos.intro))} />
          {/* signature scene right under the opening */}
          <section className="bg-[var(--fs-forest)] text-white">
            <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:py-24">
              <Reveal>
                <h2 className="text-3xl font-semibold tracking-[-0.02em] sm:text-4xl"><Words text={pt ? "Do envio ao documento selado, em segundos." : "From send to sealed document, in seconds."} /></h2>
                <p className="mt-5 text-lg leading-relaxed text-white/75">
                  {pt
                    ? "A IA encontra as linhas de assinatura, cada pessoa recebe o link por e-mail ou WhatsApp e assina no celular sem criar conta. O documento volta com IP, hora, autenticação e hash — válido pela MP 2.200-2 e Lei 14.063."
                    : "AI finds the signature lines, each person gets the link by email or WhatsApp and signs on their phone without an account. The document comes back with IP, time, authentication and hash — legally valid in Brazil."}
                </p>
                <dl className="mt-8 grid max-w-md grid-cols-3 gap-6 border-t border-white/15 pt-6 text-sm">
                  <div><dt className="text-white/60">{pt ? "Validade" : "Validity"}</dt><dd className="mt-1 font-medium">MP 2.200-2 · Lei 14.063</dd></div>
                  <div><dt className="text-white/60">{pt ? "Certificado" : "Certificate"}</dt><dd className="mt-1 font-medium">GOV.BR · ICP-Brasil</dd></div>
                  <div><dt className="text-white/60">{pt ? "Dados" : "Data"}</dt><dd className="mt-1 font-medium">LGPD · AES-256</dd></div>
                </dl>
              </Reveal>
              <Reveal variant="scale"><div className="min-w-0 rounded-2xl bg-white/[0.06] p-3 ring-1 ring-white/10 sm:p-6"><SignatureFlowMock pt={pt} /></div></Reveal>
            </div>
          </section>
        </>
      )}
      {film && (
        <>
          {heroVariant === "sales" && <SalesHero pt={pt} ctaHref={ctaHref} ctaLabel={ctaLabel} signup={signup} copy={copy} />}
          <CinematicScroll pt={pt} ctaHref={ctaHref} ctaLabel={ctaLabel} signup={signup} compact={heroVariant === "sales"} onVideo={() => setVideoUrl(youtube(videos.intro))} />
          <DynamicsImmersive pt={pt} />
        </>
      )}
      {heroVariant === "classic" && (
      <section className="mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:pb-28 lg:pt-24">
        <div>
          <h1 className={`text-4xl font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl lg:text-[3.75rem] ${forest}`}>
            <Words text={pt ? "Assinatura eletrônica com IA, pelo WhatsApp e dentro do Dynamics 365." : "E-signatures with AI, over WhatsApp and inside Dynamics 365."} />
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">
            {pt
              ? "A IA posiciona as assinaturas, o signatário assina pelo celular sem criar conta e o documento volta selado com IP, hora e hash. Validade jurídica pela MP 2.200-2 e Lei 14.063; GOV.BR quando o documento exige."
              : "AI places the signatures, the signer signs on their phone without an account and the document comes back sealed with IP, time and hash. Legally valid in Brazil; GOV.BR when the document requires it."}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href={ctaHref} data-testid="cta-hero">
              <Button size="lg" className={`${cta} h-12 w-full rounded-md px-6 text-base sm:w-auto`}>
                {ctaLabel}
              </Button>
            </a>
            <Button size="lg" variant="outline" className="h-12 w-full rounded-md border-gray-300 px-6 text-base text-gray-800 sm:w-auto" onClick={() => setVideoUrl(youtube(videos.intro))} data-testid="cta-demo">
              <Play className="mr-2 h-4 w-4" />
              {pt ? "Ver o vídeo (1 min)" : "Watch the video (1 min)"}
            </Button>
          </div>
          {signup && (
            <p className="mt-3 text-sm text-gray-500">
              {pt ? "Plano inicial gratuito, sem cartão de crédito." : "Free starter plan. Card validation only for accounts outside Brazil, no charge."}
            </p>
          )}

          <dl className="mt-10 grid max-w-xl grid-cols-3 gap-6 border-t border-black/10 pt-6 text-sm">
            <div>
              <dt className="text-gray-500">{pt ? "Validade" : "Validity"}</dt>
              <dd className="mt-1 font-medium">MP 2.200-2 · Lei 14.063</dd>
            </div>
            <div>
              <dt className="text-gray-500">{pt ? "Certificado" : "Certificate"}</dt>
              <dd className="mt-1 font-medium">GOV.BR · ICP-Brasil</dd>
            </div>
            <div>
              <dt className="text-gray-500">{pt ? "Dados" : "Data"}</dt>
              <dd className="mt-1 font-medium">LGPD · AES-256</dd>
            </div>
          </dl>
        </div>

        <Stage3D className="relative">
          <div className="fs-stage-glow" aria-hidden="true" />
          <div data-depth="9" className="fs-float pointer-events-none absolute -left-3 top-16 z-10 hidden items-center gap-1.5 rounded-full border border-black/5 bg-white px-3 py-1.5 text-xs font-medium text-[var(--fs-forest)] shadow-lg lg:flex"><Sparkles className="h-3.5 w-3.5" />{pt ? "IA posicionou 2 assinaturas" : "AI placed 2 signatures"}</div>
          <div data-depth="6" className="fs-float-slow pointer-events-none absolute -right-2 bottom-24 z-10 hidden items-center gap-1.5 rounded-full border border-black/5 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-lg lg:flex"><ShieldCheck className="h-3.5 w-3.5 text-[var(--fs-moss)]" />{pt ? "Hash SHA-256 · auditoria" : "SHA-256 hash · audit trail"}</div>
          <SignatureFlowMock pt={pt} />
        </Stage3D>
      </section>

      )}

      {!film && whoSection}

      {/* ── How it works ───────────────────────────────────────── */}
      {!film && (
        <section id="how" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-20 sm:px-8 lg:py-28">
          <Reveal className="max-w-2xl">
            <h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>
              <Words text={pt ? "Três passos. Nenhum treinamento." : "Three steps. No training."} />
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {pt ? "Do upload ao documento assinado em minutos, sem o signatário instalar nada." : "From upload to signed document in minutes, with nothing for the signer to install."}
            </p>
          </Reveal>
          <Reveal className="mt-12" variant="scale" delay={120}><StepsInteractive pt={pt} /></Reveal>
        </section>
      )}

      {!film && featuresSection}

      {/* ── Dynamics 365 ───────────────────────────────────────── */}
      <section id={film ? "dynamics-details" : "dynamics"} className="scroll-mt-24 bg-[var(--fs-d365-blue)] text-white">
        <DynamicsSwitch pt={pt}>
        <div className="mx-auto max-w-6xl px-5 pb-20 pt-8 sm:px-8 lg:pb-28">
          <Reveal><div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-end">
          <div>
            <h2 className="text-3xl font-semibold tracking-[-0.02em] sm:text-4xl">
              <Words text={pt ? "Assinaturas sem sair do Dynamics 365" : "Signatures without leaving Dynamics 365"} />
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-white/75">
              {pt
                ? "Um componente PCF numa seção do formulário: o vendedor escolhe o template, os campos vêm do registro, a IA posiciona as assinaturas e o contrato sai da própria oportunidade. Status volta pro registro; PDF assinado e auditoria ficam anexados. Qualquer entidade, inclusive customizada."
                : "A PCF component in a form section: the rep picks the template, fields come from the record, AI places the signatures and the contract goes out from the opportunity itself. Status flows back to the record; signed PDF and audit trail stay attached. Any entity, including custom ones."}
            </p>
            <ul className="mt-6 space-y-2 text-white/85">
              {(pt
                ? ["Upload de PDF/Word ou template com campos do CRM (os dois ou só um, na configuração)", "Assinantes dos contatos e usuários do registro, ou externos, com WhatsApp opcional", "IA sugere as posições; você confirma ou ajusta arrastando", "Status por assinante, reenvio por e-mail ou WhatsApp, auditoria, cancelamento e edição no FillSign", "Interface em português, inglês e espanhol"]
                : ["PDF/Word upload or a template with CRM fields (both or just one, per configuration)", "Signers from the record's contacts and users, or external, with optional WhatsApp", "AI suggests positions; you confirm or drag to adjust", "Per-signer status, resend by email or WhatsApp, audit trail, cancel and edit in FillSign", "Interface in Portuguese, English and Spanish"]
              ).map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-[var(--fs-leaf)]" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <D365Button pt={pt} />
              <Link href={WEBSITE_ROUTES.enterpriseContact} className="whitespace-nowrap text-sm text-white/80 underline underline-offset-4 hover:text-white sm:self-center">
                {pt ? "Falar com vendas" : "Talk to sales"}
              </Link>
            </div>
          </div>
          </div></Reveal>
          {!film && <Reveal className="mt-12 min-w-0" variant="scale"><PcfShowcase pt={pt} /></Reveal>}
        </div>
        <div className="mx-auto max-w-6xl px-5 pb-20 sm:px-8 lg:pb-28">
          <Reveal><div className="grid items-center gap-8 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <h3 className="text-2xl font-semibold tracking-[-0.02em]">{pt ? "Veja funcionando num registro de Conta" : "See it running on an Account record"}</h3>
              <p className="mt-3 text-white/75">{pt ? "Demo de 3 minutos no formulário padrão do Dynamics: escolha do documento ou template, assinantes vindos dos contatos, posicionamento pela IA, envio e acompanhamento." : "3-minute demo on the standard Dynamics form: document or template, signers from contacts, AI placement, sending and tracking."}</p>
              <p className="mt-3 text-sm text-white/60">{pt ? "Upload direto, templates ou os dois: você escolhe na configuração do componente." : "Direct upload, templates or both: you choose in the component configuration."}</p>
            </div>
            <VideoFacade id={videos.d365} title={pt ? "FillSign dentro do Dynamics 365 CRM" : "FillSign inside Dynamics 365 CRM"} />
          </div></Reveal>
        </div>
        </DynamicsSwitch>
      </section>

      {film && templatesSection}
      {film && whoSection}
      {film && featuresSection}

      {!film && templatesSection}

      {/* ── GOV.BR ─────────────────────────────────────────────── */}
      <section id="govbr" className="scroll-mt-24 border-y border-black/5 bg-[#F7FAF3]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-28">
          <Reveal className="max-w-2xl">
            <h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>
              <Words text={pt ? "Documentos que exigem GOV.BR, gerenciados no mesmo lugar" : "Documents that require GOV.BR, managed in the same place"} />
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {pt
                ? "Quando o documento exige assinatura gov.br ou certificado ICP-Brasil, o fluxo não sai do FillSign. O signatário assina no portal gov.br (assinatura avançada) ou com o próprio certificado ICP-Brasil (qualificada), devolve o PDF pelo link e você revisa, valida e arquiva com a mesma auditoria."
                : "When a document requires a gov.br signature or an ICP-Brasil certificate, the flow stays in FillSign. The signer signs on the gov.br portal (advanced signature) or with their own ICP-Brasil certificate (qualified), returns the PDF through the link, and you review, validate and archive with the same audit trail."}
            </p>
          </Reveal>
          <Reveal className="mt-12 min-w-0" variant="scale"><GovBrFlowMock pt={pt} /></Reveal>
        </div>
      </section>

      {/* ── Security ───────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
        <Reveal stagger><ul className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {((pt
            ? [
                [Lock, "Criptografia", "AES-256 em repouso, TLS 1.3 em trânsito."],
                [ShieldCheck, "LGPD", "Dados hospedados em nuvem, com backups e controle de acesso."],
                [Clock3, "Trilha de auditoria", "IP, data e hora, autenticação e hash em cada assinatura."],
                [Landmark, "GOV.BR", "Assinatura gov.br ou certificado ICP-Brasil quando o documento exigir."],
              ]
            : [
                [Lock, "Encryption", "AES-256 at rest, TLS 1.3 in transit."],
                [ShieldCheck, "LGPD", "Cloud-hosted data with backups and access control."],
                [Clock3, "Audit trail", "IP, timestamp, authentication and hash on every signature."],
                [Landmark, "GOV.BR", "gov.br signature or ICP-Brasil certificate when the document requires it."],
              ]) as IconCard[]
          ).map(([Icon, title, desc]) => (
            <li key={title}>
              <Icon className="h-5 w-5 text-[var(--fs-moss)]" />
              <h3 className="mt-3 font-semibold">{title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-gray-600">{desc}</p>
            </li>
          ))}
        </ul></Reveal>
      </section>

      {/* ── Pricing ────────────────────────────────────────────── */}
      <section id="pricing" className="scroll-mt-24 border-y border-black/5 bg-[#F7FAF3]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-28">
          <Reveal><div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>
              <Words text={pt ? "Comece grátis, cresça quando precisar" : "Start free, grow when you need to"} />
            </h2>
            <Link href={WEBSITE_ROUTES.pricing} className="text-sm text-gray-700 underline underline-offset-4 hover:text-gray-900">
              {pt ? "Comparar todos os planos" : "Compare all plans"}
            </Link>
          </div></Reveal>
          <Reveal className="mt-10" stagger><div className="grid gap-5 md:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`flex flex-col rounded-lg border bg-white p-6 ${plan.highlight ? "border-[var(--fs-moss)] shadow-[0_20px_50px_-30px_rgba(45,80,22,0.5)]" : "border-black/10"}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{plan.name}</p>
                  {plan.highlight && (
                    <span className="rounded-full bg-[#F3F8EC] px-2.5 py-1 text-xs font-medium text-[var(--fs-forest)]">
                      {pt ? "Mais escolhido" : "Most popular"}
                    </span>
                  )}
                </div>
                <p className="mt-4 text-3xl font-semibold tracking-tight">
                  {plan.price}
                  {plan.period && <span className="text-base font-normal text-gray-500">{plan.period}</span>}
                </p>
                <p className="mt-2 flex-1 text-sm text-gray-600">{plan.desc}</p>
                {plan.contactSales ? (
                  <Link href={WEBSITE_ROUTES.enterpriseContact} className="mt-6">
                    <Button variant="outline" className="w-full rounded-md border-gray-300">{pt ? "Falar com vendas" : "Talk to sales"}</Button>
                  </Link>
                ) : (
                  <a href={ctaHref} className="mt-6">
                    <Button className={`w-full rounded-md ${plan.highlight ? cta : "border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"}`}>
                      {signup ? (pt ? "Começar" : "Get started") : ctaLabel}
                    </Button>
                  </a>
                )}
              </div>
            ))}
          </div></Reveal>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────── */}
      <section id="faq" className="mx-auto max-w-3xl scroll-mt-24 px-5 py-20 sm:px-8 lg:py-28">
        <Reveal><h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>
          {pt ? "Perguntas frequentes" : "Frequently asked questions"}
        </h2>
        <Accordion type="single" collapsible className="mt-8">
          {faq.map(([q, a], i) => (
            <AccordionItem key={q} value={`faq-${i}`} className="border-black/10">
              <AccordionTrigger className="py-5 text-left text-base font-medium hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="leading-relaxed text-gray-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion></Reveal>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────── */}
      <section className="bg-[var(--fs-forest)] text-white">
        <Reveal><div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-5 py-16 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-[-0.02em]">{pt ? "Envie seu primeiro documento hoje." : "Send your first document today."}</h2>
            <p className="mt-2 text-white/75">{signup ? (pt ? "Leva menos de dois minutos." : "Takes under two minutes.") : (pt ? "Conte o seu cenário e montamos o ambiente." : "Tell us your scenario and we'll set up the environment.")}</p>
          </div>
          <a href={ctaHref} data-testid="cta-final">
            <Button size="lg" className="h-12 rounded-md bg-white px-7 text-base text-[var(--fs-forest)] hover:bg-white/90">
              {ctaLabel}
            </Button>
          </a>
        </div></Reveal>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="border-t border-black/5 bg-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2">
              <img src="/favicon.png" alt="" className="h-8 w-8" />
              <BrandText className="text-lg" />
            </div>
            <p className="mt-3 max-w-xs text-sm text-gray-600">
              {pt ? "Assinatura eletrônica com validade jurídica, pelo WhatsApp, e-mail e Dynamics 365." : "Legally valid e-signatures over WhatsApp, email and Dynamics 365."}
            </p>
          </div>
          <div className="text-sm">
            <p className="font-semibold">{pt ? "Produto" : "Product"}</p>
            <ul className="mt-3 space-y-2 text-gray-600">
              <li><a href="#features" className="hover:text-gray-900">{pt ? "Recursos" : "Features"}</a></li>
              <li><Link href={WEBSITE_ROUTES.pricing} className="hover:text-gray-900">{pt ? "Preços" : "Pricing"}</Link></li>
              <li><Link href={WEBSITE_ROUTES.dynamics365} className="hover:text-gray-900">Dynamics 365</Link></li>
              <li><Link href={WEBSITE_ROUTES.apiDocs} className="hover:text-gray-900">API</Link></li>
            </ul>
          </div>
          <div className="text-sm">
            <p className="font-semibold">{pt ? "Suporte" : "Support"}</p>
            <ul className="mt-3 space-y-2 text-gray-600">
              <li><Link href={WEBSITE_ROUTES.help} className="hover:text-gray-900">{pt ? "Central de ajuda" : "Help center"}</Link></li>
              <li><Link href={WEBSITE_ROUTES.validate} className="hover:text-gray-900">{pt ? "Validar documento assinado" : "Validate a signed document"}</Link></li>
              <li><Link href={WEBSITE_ROUTES.enterpriseContact} className="hover:text-gray-900">{pt ? "Falar com vendas" : "Contact sales"}</Link></li>
              {WHATSAPP_URL && <li><a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="hover:text-gray-900">WhatsApp</a></li>}
            </ul>
          </div>
          <div className="text-sm">
            <p className="font-semibold">Legal</p>
            <ul className="mt-3 space-y-2 text-gray-600">
              <li><Link href={WEBSITE_ROUTES.terms} className="hover:text-gray-900">{pt ? "Termos de uso" : "Terms of use"}</Link></li>
              <li><Link href={WEBSITE_ROUTES.privacy} className="hover:text-gray-900">{pt ? "Privacidade" : "Privacy"}</Link></li>
              <li><Link href={WEBSITE_ROUTES.license} className="hover:text-gray-900">{pt ? "Licença" : "License"}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-black/5">
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-5 py-5 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <p>© {new Date().getFullYear()} FillSign. {pt ? "Todos os direitos reservados." : "All rights reserved."}</p>
            <p>{pt ? "Feito no Brasil." : "Made in Brazil."}</p>
          </div>
        </div>
      </footer>

      <VideoModal isOpen={!!videoUrl} onClose={() => setVideoUrl(null)} videoUrl={videoUrl || undefined} />
    </div>
  );
}
